import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ShieldAlert, CheckCircle2, UserCheck, Send, Loader2, Info } from "lucide-react";
import { Button } from "../ui/button";

/**
 * Inline banner shown on the application detail page when the attorney
 * verification gate has held the underwriting email.
 *
 * Props:
 *   application    — the app doc from GET /applications/{id}
 *   onResolved     — callback fired after a resolve action succeeds
 */
export default function VerificationBanner({ application, onResolved }) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null); // pending decision

  const isHeld = application?.attorney_verification?.status === "held";

  useEffect(() => {
    if (!isHeld || !application?.id) return;
    setLoading(true);
    api.get(`/comms/verifications/${application.id}`)
      .then((r) => setCtx(r.data))
      .catch(() => setCtx(null))
      .finally(() => setLoading(false));
  }, [isHeld, application?.id]);

  if (!isHeld) return null;

  const reasonLabel = {
    invalid_or_disposable_email: "Attorney email is invalid or from a disposable domain.",
    attorney_name_matches_but_email_differs:
      "An attorney with a similar name is already on file, but the email address differs. This may be a typo or a spoofing attempt.",
  }[application?.attorney_verification?.reason] || "This attorney could not be verified automatically.";

  const resolve = async (decision) => {
    if (!ctx) return;
    setPending(decision);
    try {
      await api.post("/comms/verifications/resolve", {
        application_id: application.id,
        decision,
        plaintiff_email: ctx.entered.email,
        on_file_email: ctx.on_file?.email || null,
      });
      toast.success(
        {
          use_verified: `Sent to verified attorney: ${ctx.on_file?.email}`,
          use_plaintiff: `Sent to plaintiff-provided address: ${ctx.entered.email}`,
          update_stored: `Stored email updated to ${ctx.entered.email} and notified.`,
        }[decision] || "Verification resolved."
      );
      onResolved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve verification.");
    } finally {
      setPending(null);
    }
  };

  return (
    <div
      className="border-2 border-[#F59E0B] bg-[#FFFBEB] rounded-2xl p-6"
      data-testid="verification-banner"
    >
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-full bg-[#FEF3C7] border border-[#FCD34D] flex items-center justify-center shrink-0">
          <ShieldAlert className="w-5 h-5 text-[#B45309]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-lg font-semibold text-[#92400E]">
              Attorney Verification Required
            </h2>
            <span className="inline-flex items-center px-2 h-5 text-[10px] uppercase tracking-wider rounded border bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]">
              Underwriting email held
            </span>
          </div>
          <p className="text-sm text-[#78350F] mt-1.5">{reasonLabel}</p>

          {loading ? (
            <div className="mt-4 text-sm text-[#92400E] inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading verification context…
            </div>
          ) : ctx ? (
            <>
              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <ContactCard
                  title="Entered by plaintiff"
                  tone="entered"
                  name={ctx.entered?.name}
                  email={ctx.entered?.email}
                  firm={ctx.entered?.firm}
                  phone={ctx.entered?.phone}
                />
                {ctx.on_file ? (
                  <ContactCard
                    title="On-file attorney"
                    tone="verified"
                    name={ctx.on_file?.name}
                    email={ctx.on_file?.email}
                    firm={ctx.on_file?.firm_name}
                  />
                ) : (
                  <div className="border border-dashed border-[#E5E7EB] rounded-lg p-4 text-sm text-[#6B7280]">
                    <div className="uppercase-label mb-1">On-file attorney</div>
                    <div className="italic">No matching attorney on file.</div>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-2 mt-5">
                {ctx.on_file?.email && (
                  <Button
                    onClick={() => resolve("use_verified")}
                    disabled={!!pending}
                    className="bg-[#059669] hover:bg-[#047857] text-white"
                    data-testid="verify-use-verified-btn"
                  >
                    {pending === "use_verified" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Use verified email
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (!window.confirm(
                      `Send underwriting email to plaintiff-provided address "${ctx.entered?.email}"? ` +
                      "This will NOT update stored attorney records."
                    )) return;
                    resolve("use_plaintiff");
                  }}
                  disabled={!!pending}
                  variant="outline"
                  className="border-[#B45309] text-[#92400E] hover:bg-[#FEF3C7]"
                  data-testid="verify-use-plaintiff-btn"
                >
                  {pending === "use_plaintiff" ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Use plaintiff email
                </Button>
                {ctx.on_file?.email && (
                  <Button
                    onClick={() => {
                      if (!window.confirm(
                        `Permanently update the stored email for ${ctx.on_file?.name || "this attorney"} ` +
                        `from "${ctx.on_file?.email}" to "${ctx.entered?.email}"? All future emails for this attorney will use the new address.`
                      )) return;
                      resolve("update_stored");
                    }}
                    disabled={!!pending}
                    variant="outline"
                    data-testid="verify-update-stored-btn"
                  >
                    {pending === "update_stored" ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <UserCheck className="w-4 h-4 mr-2" />
                    )}
                    Update stored email
                  </Button>
                )}
              </div>

              <div className="mt-4 flex items-start gap-2 text-xs text-[#78350F] bg-[#FEF3C7] rounded-lg px-3 py-2">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Underwriting email will fire automatically to your chosen address after resolution. The plaintiff confirmation and internal alert have already been sent.
                </span>
              </div>
            </>
          ) : (
            <div className="mt-4 text-sm text-[#78350F]">
              Could not load verification context. Try refreshing the page.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ContactCard({ title, tone, name, email, firm, phone }) {
  const toneClass =
    tone === "verified"
      ? "border-[#A7F3D0] bg-[#ECFDF5]"
      : "border-[#FDE68A] bg-[#FFFBEB]";
  const label =
    tone === "verified"
      ? "bg-[#D1FAE5] text-[#065F46] border-[#6EE7B7]"
      : "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]";
  return (
    <div className={`border rounded-lg p-4 ${toneClass}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="uppercase-label">{title}</div>
        <span className={`inline-flex items-center px-2 h-5 text-[10px] uppercase tracking-wider rounded border ${label}`}>
          {tone === "verified" ? "Verified" : "Unverified"}
        </span>
      </div>
      <div className="text-sm text-[#0A2540] font-medium">{name || "—"}</div>
      <div className="text-sm text-[#374151] break-all">{email || "—"}</div>
      {firm && <div className="text-xs text-[#6B7280] mt-1">{firm}</div>}
      {phone && <div className="text-xs text-[#6B7280]">{phone}</div>}
    </div>
  );
}
